import { useTranslation } from 'react-i18next';
import { Modal, Button, TEOLoader } from '@teo/components';
import { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Portal } from '@/components/Portal';
import { axios } from '@/axios';
import { config } from '@/config';
import { useGet } from '@/query/fetchHooks';
import { Tag, TrashIcon } from '@teo/components/icons';
import { PaginationMultiselectWithTags } from '@/components/PaginationMultiselectWithTags';
import i18n from 'i18next';
import { newUid, getTagsFromImageUrl } from '@/util';
import { AITags } from './LibraryModal/AITag';

export const AddImageToLibrary = ({
  imageUrl,
  imageName,
  isOpenAddLibraryModal,
  setIsOpenAddLibraryModal,
  editImage = null,
  setEditImage = null,
  edit = false,
  setRefresh = null,
  refetchLibrary,
}) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;
  const organisationId = authUser?.activeOrganisationId;
  const [selectTags, setSelectTags] = useState([]);
  const [imgTags, setImgTags] = useState([]);
  const [exists, setExists] = useState(edit ? edit : false);
  const [filter, setFilter] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [activeImage, setActiveImage] = useState(editImage);

  const [imageTagsLoaded, setImageTagsLoaded] = useState(false);

  const aiRequestedRef = useRef(false);

  const [load, setLoad] = useState(edit ? edit : false);

  const [aiTags, setAiTags] = useState([]);
  const [aiTagsLoading, setAiTagsLoading] = useState(false);
  const [libraryChecked, setLibraryChecked] = useState(edit ? true : false);
  const [isloadedAITags, setIsloadedAITags] = useState(false);

  const [name, setName] = useState(imageName);

  const closeModal = () => {
    setIsOpenAddLibraryModal(!isOpenAddLibraryModal);
  };

  const url = imageUrl;
  const isAmazonAws = url?.includes('amazonaws.com');
  const isCloudFront = imageUrl?.includes('cloudfront.net');

  let uid;
  let imgName;
  let imgPrev;
  if (isAmazonAws) {
    const fileName = url.split('/').pop();
    imgPrev = fileName.substring(fileName.indexOf('-') + 1);
    imgName = imgPrev.substring(0, imgPrev.lastIndexOf('.'));
    const decodedURL = decodeURIComponent(url);
    const fileTitle = decodedURL.split('/').pop();
    uid = fileTitle.split('-')[0];
  }

  if (isCloudFront) {
    const fileName = url.split('/').pop();
    imgPrev = fileName.substring(fileName.indexOf('-') + 1);
    imgName = imgPrev.substring(0, imgPrev.lastIndexOf('.'));
  }

  useGet(`/imagelibraries`, {
    enabled: !edit,
    organisationId: organisationId,
    imageUid: isAmazonAws ? uid : isCloudFront ? imgName : uid,
    onSuccess: (result) => {
      if (result?.data?.length > 0) {
        setExists(true);
        setActiveImage(result.data[0]);
      } else {
        setExists(false);
        setActiveImage(null);
      }
      setLibraryChecked(true);
      setLoad(true);
    },

    onError: () => {
      setExists(false);
      setActiveImage(null);
      setLibraryChecked(true);
      setLoad(true);
    },
  });

  const fetchAITags = async (existingTags = []) => {
    if (!imageUrl || aiRequestedRef.current) return;

    aiRequestedRef.current = true;

    setAiTagsLoading(true);

    const currentExistingTags = existingTags
      .map((el) => el?.label || el?.tag || el)
      .filter(Boolean);

    const authId = auth?.user?.id;
    const result = await getTagsFromImageUrl(
      imageUrl,
      currentExistingTags,
      authId
    );

    setAiTags(result);
    setAiTagsLoading(false);
  };

  useEffect(() => {
    if (!libraryChecked) return;
    if (!isloadedAITags) return;

    setSelectTags([]);
    setActiveTags([]);
    setImgTags([]);
    setAiTags([]);

    aiRequestedRef.current = false;

    if (activeImage?.id) {
      setImageTagsLoaded(false);

      axios
        .get(
          `${config.backend}/imagetags/?imageId=${activeImage.id}&search=tag:%`
        )
        .then((response) => {
          setImgTags(response.data || []);
        })
        .finally(() => {
          setImageTagsLoaded(true);
        });
    } else {
      fetchAITags([]);
    }
  }, [libraryChecked, activeImage?.id, isloadedAITags]);

  useEffect(() => {
    if (!activeImage?.id) return;
    if (!imageTagsLoaded) return;

    const nextTags = (imgTags || []).map((tag) => {
      const value = tag?.tag?.split('tag:')[1];

      return {
        id: tag?.id,
        key: value,
        label: value,
        tag: value,
      };
    });

    setActiveTags(nextTags);
    setSelectTags(nextTags);

    fetchAITags(nextTags);
  }, [imageTagsLoaded, imgTags, activeImage?.id]);

  const buildImageTagsString = (tags = []) =>
    tags.map((item) => `tag:${item?.tag || item?.label}`).join(';;');

  const handleTags = async () => {
    const nextActiveTags = selectTags.map((tag) => ({
      id: tag?.id,
      key: tag?.label,
      label: tag?.label,
      tag: tag?.tag || tag?.label,
    }));

    if (exists) {
      if (selectTags?.length > 0) {
        for (const addObj2 of selectTags) {
          const existingObj = activeTags?.find(
            (obj1) => obj1?.id === addObj2?.id
          );
          if (!existingObj) {
            axios.post(`${config.backend}/imagetags`, {
              imageId: activeImage.id,
              tag: 'tag:' + addObj2?.label,
              id: addObj2?.id,
              organisationId,
            });
            setActiveTags((prev) => [
              ...prev,
              {
                id: addObj2?.id,
                key: addObj2?.label,
                label: addObj2?.label,
                tag: addObj2?.label,
              },
            ]);
          }
        }
      }

      if (activeTags?.length > 0) {
        for (const removedTag of activeTags) {
          const existingObj = selectTags?.find(
            (obj1) => obj1?.id === removedTag?.id
          );
          if (!existingObj) {
            axios.delete(
              `${config.backend}/imagetag/${activeImage.id}/${removedTag?.id}`,
              { params: { organisationId } }
            );
            setActiveTags((prev) =>
              prev?.filter((el) => el?.id !== removedTag?.id)
            );
          }
        }
      }

      await axios.put(
        `${config.backend}/imagelibrary/${activeImage.id}`,
        { tags: buildImageTagsString(nextActiveTags) },
        { params: { organisationId } }
      );
      setEditImage && setEditImage(null);
      setRefresh && setRefresh(newUid(20));
      setIsOpenAddLibraryModal(false);
    }
  };

  const handleAddTag = () => {
    if (filter) {
      const promises = [];
      promises.push(
        axios.post(`${config.backend}/tags`, {
          tag: filter,
          label: filter,
          key: Math.floor(Math.random() * 9999999999999),
          organisationId: auth?.user?.activeOrganisationId,
          lang: auth?.user?.lang,
        })
      );
      Promise.allSettled(promises).then((results) => {
        setSelectTags((prev) => [
          ...prev,
          {
            key: filter,
            label: filter,
            tag: filter,
            id: results[0].value?.data?.id,
          },
        ]);
        setFilter('');
      });
    }
  };

  const handleAddImage = async () => {
    if (!imageUrl || !organisationId) return;

    const arr = selectTags?.map((tag) => tag.tag) || [];
    const imgTags = (arr || []).map((item) => `tag:${item}`).join(';;');

    const newLibraryImage = await axios.post(
      `${config.backend}/imagelibraries`,
      {
        imageUrl,
        imageName: imgName,
        imageUid: isAmazonAws ? uid : isCloudFront ? imgName : uid,
        imageType: imageUrl.split('.').pop(),
        organisationId,
        tags: imgTags,
      }
    );

    const imageId = newLibraryImage?.data?.id;

    if (imageId && selectTags?.length) {
      for (const tag of selectTags) {
        await axios.post(`${config.backend}/imagetags`, {
          imageId: imageId,
          tag: 'tag:' + tag?.label,
          id: tag?.id,
          organisationId,
        });
      }
    }
    refetchLibrary && refetchLibrary();
    setIsOpenAddLibraryModal(false);
  };

  // console.log(selectTags);

  return (
    <Portal root="body">
      <div className="z-10 bg-white">
        <Modal isOpen={isOpenAddLibraryModal}>
          <Modal.Header onClose={() => closeModal()}>
            {load &&
              (exists
                ? t('pages.create_library.edit_image')
                : t('pages.create_library.add_image'))}
          </Modal.Header>
          <Modal.Body>
            <div className=" -ml-6 -mr-6 max-h-[50vh]  overflow-y-scroll px-6">
              {load ? (
                <div className="flex flex-col sm:flex-row">
                  <div className="flex flex-col pr-2 sm:basis-1/2">
                    <img
                      src={imageUrl}
                      alt="image"
                      className="max-h-[165px] w-full max-w-[265px] rounded-lg border border-grey-01 object-cover sm:max-h-[365px] sm:max-w-[365px]"
                    />
                    <p
                      className="mt-4 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-grey-08"
                      style={{ maxWidth: '350px' }}
                    >
                      {name || imgPrev}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:basis-1/2 sm:pl-2">
                    <h4 className="text-base">{t('links.tags')}</h4>
                    {exists ? (
                      <p className="text-sm text-grey-06">
                        {t('add_img_to_library.add_tags_m_e')}
                      </p>
                    ) : (
                      <p className="text-sm text-grey-06">
                        {t('add_img_to_library.add_tags_m')}
                      </p>
                    )}
                    <div className="relative z-[10] flex items-center">
                      <Tag className="mr-2 h-[17.5px] w-[17.5px] text-[#6d6d72]" />

                      <div
                        className="w-full"
                        onSelect={() =>
                          !isloadedAITags && setIsloadedAITags(true)
                        }
                      >
                        <PaginationMultiselectWithTags
                          value={selectTags}
                          onChange={(value) => {
                            setSelectTags(value);
                          }}
                          filter={filter}
                          setFilter={setFilter}
                          keyFn={(row) => row?.tag}
                          fetch={useGet.bind(
                            null,
                            `/tags?lang=${i18n.language}&organisationId=${organisationId},OR,organisationId=null`
                          )}
                          defaultSort={[{ id: 'tag', desc: false }]}
                          render={(row) => {
                            return row?.tag;
                          }}
                          customStyles="bg-white"
                          addTagLabel={`${filter} ${t(
                            'pages.create_edit_course.addTagLabel'
                          )}`}
                          handleAddTag={handleAddTag}
                          placeholder={
                            selectTags?.length > 0
                              ? ''
                              : t('pages.create_edit_course.tags_placeholder')
                          }
                        />
                      </div>
                    </div>
                    {isloadedAITags && (
                      <AITags
                        tags={aiTags}
                        loading={aiTagsLoading}
                        setTags={setAiTags}
                        setSelectTags={setSelectTags}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex w-full">
                  <div className="m-auto">
                    <TEOLoader
                      loaderSrc="/images/TEOloader.gif"
                      className="w-[120px]"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="relative -ml-6 -mr-6 mt-6 flex flex-row items-center gap-3 border-t px-6 pt-6">
              {!exists ? (
                <p className="text-sm text-grey-06">
                  {t('add_img_to_library.add_least_tag')}
                </p>
              ) : (
                <Button
                  className="flex items-center gap-2 shadow-none"
                  variant="warning"
                  onClick={async () => {
                    await axios.delete(
                      `${config.backend}/imagelibrary/${activeImage?.id}`,
                      { params: { organisationId } }
                    );
                    setEditImage && setEditImage(null);
                    setRefresh && setRefresh(newUid(20));
                    refetchLibrary && refetchLibrary();
                    setIsOpenAddLibraryModal(false);
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  {t('button.delete')}
                </Button>
              )}

              <Button
                className="ml-auto"
                type="button"
                variant="outline"
                onClick={() => {
                  closeModal();
                }}
              >
                {t('widgets.upload_buttons.cancel_button')}
              </Button>
              {exists ? (
                <Button
                  type="button"
                  onClick={() => {
                    handleTags();
                  }}
                >
                  {t('button.save')}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    handleAddImage();
                  }}
                >
                  {t('add_img_to_library.save_to_library')}
                </Button>
              )}
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </Portal>
  );
};
